/**
 * 规则引擎 —— 对应技术设计 §6.2「规则表达」。
 *
 * 规则是**声明式数据**（实验包里的 `rules.json`），引擎只负责编译与执行。
 * 新增实验只写规则，不改引擎（§22 红线 3 / 红线 12）。
 *
 *   when: 条件数组（AND 语义，见 condition-evaluator）
 *   then: 动作数组
 *
 * 动作四类：
 *   set      改状态域或器材 state
 *   emit     发引擎内部事件（**不是**出网事件，出网目录 S0 才建）
 *   animate  请求播放一段现象（M1c 的 animation-engine 消费）
 *   knowledge 触发一个知识点（M1c 的 knowledge-engine 消费）
 */

import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { updateObject } from "../eom/assembly-graph.js";
import type { CompiledCondition, EvalContext } from "./condition-evaluator.js";
import { compileAll } from "./condition-evaluator.js";
import type { ProcessState, ProcessTable } from "./process-engine.js";

const PROCESS_STATES: readonly ProcessState[] = ["idle", "running", "paused", "done", "aborted"];

function asProcessState(value: unknown, ruleContext: string): ProcessState {
  const text = String(value);
  if (!(PROCESS_STATES as readonly string[]).includes(text)) {
    throw new Error(
      `${ruleContext} 想把过程状态设成 "${text}"，可用值：${PROCESS_STATES.join(", ")}`,
    );
  }
  return text as ProcessState;
}

export interface SetAction {
  set: string;
  value: unknown;
}
export interface EmitAction {
  emit: string;
  payload?: Record<string, unknown>;
}
export interface AnimateAction {
  animate: string;
  params?: Record<string, unknown>;
}
export interface KnowledgeAction {
  knowledge: string;
}

export type RuleAction = SetAction | EmitAction | AnimateAction | KnowledgeAction;

export interface RuleDefinition {
  id: string;
  when: string[];
  then: RuleAction[];
  /** 安全类规则的严重度（§14.3），由 safety-engine 消费 */
  severity?: "info" | "warn" | "danger";
  /** 规则只在这些步骤内生效。缺省 = 任何步骤 */
  steps?: string[];
  /** true = 条件满足过一次就不再触发（避免每帧重复发事件） */
  once?: boolean;
}

export interface CompiledRule {
  definition: RuleDefinition;
  matches: CompiledCondition;
}

export interface RuleOutcome {
  graph: AssemblyGraph;
  state: Record<string, Record<string, unknown>>;
  processes: ProcessTable;
  emitted: EmitAction[];
  animations: AnimateAction[];
  knowledge: KnowledgeAction[];
  firedRuleIds: string[];
}

/** 编译整份规则表。任何一条条件写错都会抛错，**不静默跳过**。 */
export function compileRules(definitions: readonly RuleDefinition[]): CompiledRule[] {
  return definitions.map((definition) => {
    try {
      return { definition, matches: compileAll(definition.when) };
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      throw new Error(`规则 "${definition.id}" 编译失败：${detail}`);
    }
  });
}

/**
 * `set` 的目标路径三种形态：
 *   `object(<id>).state.<键>`   改某件器材的状态
 *   `process.<id>.state`        改反应过程状态
 *   `<域>.<键>`                 改引擎状态域，如 `system.airTightness`
 */
function applySet(action: SetAction, outcome: RuleOutcome, ruleId: string): void {
  const objectMatch = /^object\(([^)]+)\)\.state\.(.+)$/.exec(action.set);
  if (objectMatch) {
    const objectId = objectMatch[1]!.trim();
    const key = objectMatch[2]!.trim();
    const node = outcome.graph.nodes[objectId];
    if (!node) return;
    outcome.graph = updateObject(outcome.graph, objectId, {
      state: { ...node.state, [key]: action.value },
    });
    return;
  }

  const processMatch = /^process[.(]([^).]+)\)?\.(state|progress)$/.exec(action.set);
  if (processMatch) {
    const processId = processMatch[1]!.trim();
    const field = processMatch[2]! as "state" | "progress";
    const current = outcome.processes[processId] ?? { state: "idle" as const };
    outcome.processes = {
      ...outcome.processes,
      [processId]:
        field === "state"
          ? { ...current, state: asProcessState(action.value, `规则 "${ruleId}"`) }
          : { ...current, progress: Number(action.value) },
    };
    return;
  }

  const parts = action.set.split(".");
  if (parts.length >= 2) {
    const [domain, ...rest] = parts;
    const key = rest.join(".");
    outcome.state = {
      ...outcome.state,
      [domain!]: { ...(outcome.state[domain!] ?? {}), [key]: action.value },
    };
  }
}

export interface RuleRunOptions {
  /** 当前 Mission 步骤 id，用于过滤 `steps` 限定的规则 */
  stepId?: string;
  /** 已经触发过的 once 规则 id */
  firedOnce: ReadonlySet<string>;
}

/**
 * 跑一轮规则。返回**新的**图与状态，不就地修改传入的 context。
 *
 * 规则按声明顺序求值，前一条的 `set` 对后一条可见 —— 这让「温度到 → 反应开始 →
 * 反应开始 → 冒泡」这类链式规则可以写成两条独立规则，而不必写成一条大规则。
 */
export function runRules(
  rules: readonly CompiledRule[],
  context: EvalContext,
  options: RuleRunOptions,
): RuleOutcome {
  const outcome: RuleOutcome = {
    graph: context.graph,
    state: { ...context.state },
    processes: { ...context.processes },
    emitted: [],
    animations: [],
    knowledge: [],
    firedRuleIds: [],
  };

  for (const rule of rules) {
    const { definition } = rule;
    if (definition.once && options.firedOnce.has(definition.id)) continue;
    if (definition.steps && options.stepId && !definition.steps.includes(options.stepId)) continue;

    const current: EvalContext = {
      graph: outcome.graph,
      state: outcome.state,
      processes: outcome.processes,
      events: context.events,
    };
    if (!rule.matches(current)) continue;

    outcome.firedRuleIds.push(definition.id);
    for (const action of definition.then) {
      if ("set" in action) applySet(action, outcome, definition.id);
      else if ("emit" in action) outcome.emitted.push(action);
      else if ("animate" in action) outcome.animations.push(action);
      else if ("knowledge" in action) outcome.knowledge.push(action);
    }
  }

  return outcome;
}
